import { styled } from "@linaria/react";
import classNames from "classnames";
import React from "react";
import { NavLink } from "react-router-dom";
import { color, fonts } from "..";
import useEnsuredForwardedRef from "../lib/hooks/useEnsuredForwardedRef";

const ACTIVE_CN = "active";

export interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  disabled?: boolean;
  to?: string;
  exact?: boolean;
  hash?: string;
  active?: boolean;
}

const Link = (
  {
    to,
    exact,
    className,
    disabled,
    onClick,
    href,
    tabIndex: tabIndexProp,
    onKeyDown,
    active, // override default behavior
    ...props
  }: LinkProps,
  forwardedRef: React.Ref<HTMLAnchorElement>,
) => {
  const cn = classNames(className, { disabled });
  const ref = useEnsuredForwardedRef(forwardedRef);
  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (disabled) {
        event.preventDefault();
        return;
      }
      if (onClick) onClick(event);
      if (!href && !to) event.preventDefault();
    },
    [disabled, href, onClick, to],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (!disabled && e.key === " ") {
        ref.current?.click();
        e.preventDefault();
      }
      if (onKeyDown) {
        onKeyDown(e);
      }
      if (disabled) e.stopPropagation();
    },
    [disabled, onKeyDown, ref],
  );

  const tabIndex = tabIndexProp === undefined ? (disabled ? -1 : undefined) : tabIndexProp;

  return to ? (
    <NavLink
      ref={ref}
      to={to}
      tabIndex={tabIndex}
      end={exact}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={({ isActive }) =>
        classNames([
          cn,
          {
            [ACTIVE_CN]: active ?? isActive,
          },
        ])
      }
      {...props}
    />
  ) : (
    <a
      ref={ref}
      href={href || ""}
      tabIndex={tabIndex}
      className={cn}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      {...props}
    />
  );
};

const LinkMemo = React.memo(React.forwardRef<HTMLAnchorElement, LinkProps>(Link));
export default LinkMemo;

// Link used for action text
export const ActionLink = styled(LinkMemo)`
  color: ${color.lightNitrogen500_Main};
  font: ${fonts.body1};
  font-weight: 500;
  :hover {
    text-decoration: underline;
  }
`;
